import React, { useMemo } from 'react'
import { NavigationButton, SettingsControlTile } from '../common'
import StyledSwitch from '@e3dc-react/shell/Elements/Switch/StyledSwitch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFormContext, useWatch } from 'react-hook-form'
import { Ai360SettingsFormValues } from '../../types'
import { Extension } from 'src/Libs/installation-extensions'
import { TariffsModeModel } from '../../model'

interface Props {
  onClick?: () => void
  tariffsMode: TariffsModeModel | null
  activeExtension: Extension | undefined
  containerClassName?: string
}

/**
 * component to activate/deactivate Grid feature and navigate to its settings
 */
const GridSettingsControlTile: React.FC<Props> = ({ onClick, tariffsMode, activeExtension, containerClassName }) => {
  const { t } = useCustomTranslation()
  const { control, setValue } = useFormContext<Ai360SettingsFormValues>()
  const isAI360Active = useWatch({ control, name: 'isAI360Active' })
  const isChargingFromGridActive = useWatch({ control, name: 'isChargingFromGridActive' })

  const description = useMemo(() => {
    if (!isChargingFromGridActive) {
      return undefined
    }

    if (isAI360Active && isChargingFromGridActive && tariffsMode?.mode === 'STATIC') {
      return t('ai360.settings-page.grid-settings.incomplete-configuration')
    }

    if (tariffsMode?.mode === 'DYNAMIC' && !tariffsMode.vendorName) {
      return t('ai360.settings-page.grid-settings.electricity-tariff.custom-tariff')
    }

    if (activeExtension) {
      return t('ai360.settings-page.charging-from-grid.description', { extensionName: activeExtension.name })
    }

    return undefined
  }, [tariffsMode, isChargingFromGridActive, t, activeExtension, isAI360Active])

  const handleToggle = (): void => {
    const newValue = !isChargingFromGridActive
    setValue('isChargingFromGridActive', newValue, { shouldDirty: true })

    // SG-Ready can work only with activated Grid feature
    if (newValue === false) {
      setValue('isSGReadyActive', false, { shouldDirty: true })
    }
  }

  const handleNavigation = (): void => {
    onClick?.()
  }

  const prefix = <StyledSwitch checked={isChargingFromGridActive} onChange={handleToggle} disabled={!isAI360Active} />
  const suffix = <NavigationButton onClick={handleNavigation} />

  return (
    <SettingsControlTile
      title={t('ai360.settings-page.charging-from-grid.title')}
      description={description}
      prefix={prefix}
      suffix={suffix}
      containerClassName={containerClassName}
    />
  )
}

export default GridSettingsControlTile
